<template>
  <div v-if="open" class="modal-backdrop" @click.self="$emit('cancel')">
    <div class="modal-card" role="dialog" aria-modal="true" :aria-labelledby="titleId">
      <h2 :id="titleId" class="modal-title">New group</h2>
      <label class="modal-label" :for="inputId">Group name</label>
      <input
        :id="inputId"
        ref="inputEl"
        v-model="name"
        type="text"
        class="modal-input"
        :class="{ 'modal-input--error': !!localError }"
        :aria-invalid="!!localError"
        placeholder="e.g. Finance"
        @input="onInput"
        @keydown.enter.prevent="submit"
        @keydown.esc="$emit('cancel')"
      />
      <p v-if="localError" class="modal-error" role="alert">{{ localError }}</p>
      <div class="modal-actions">
        <button type="button" class="btn btn--secondary" @click="$emit('cancel')">Cancel</button>
        <button
          type="button"
          class="btn btn--primary"
          :disabled="!name.trim() || busy"
          @click="submit"
        >
          {{ busy ? 'Creating…' : 'Create' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  error: { type: String, default: '' },
  busy: { type: Boolean, default: false },
});
const emit = defineEmits(['confirm', 'cancel']);

const uid = Math.random().toString(36).slice(2);
const titleId = `create-group-modal-${uid}`;
const inputId = `create-group-input-${uid}`;

const name = ref('');
const localError = ref('');
const inputEl = ref(null);

watch(() => props.error, v => { localError.value = v; });

watch(() => props.open, isOpen => {
  if (isOpen) {
    name.value = '';
    localError.value = '';
    nextTick(() => inputEl.value?.focus?.());
  }
});

function onInput() {
  // Clear a stale server-side error as soon as the user edits the name.
  if (localError.value) localError.value = '';
}

function submit() {
  const trimmed = name.value.trim();
  if (!trimmed || props.busy) return;
  emit('confirm', trimmed);
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem 1.75rem;
  width: min(440px, 90vw);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.18);
}

.modal-title {
  margin: 0 0 0.75rem;
  font-size: 1.125rem;
  color: #0f172a;
}

.modal-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
  font-weight: 500;
  color: #334155;
}

.modal-input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.65rem;
  font-size: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  color: #0f172a;
}

.modal-input:focus {
  outline: none;
  border-color: var(--highlights, #2563eb);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.modal-input--error {
  border-color: #dc2626;
}

.modal-error {
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: #dc2626;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn--secondary {
  background: #f1f5f9;
  color: #334155;
  border-color: #cbd5e1;
}

.btn--secondary:hover {
  background: #e2e8f0;
}

.btn--primary {
  background: var(--highlights, #2563eb);
  color: #ffffff;
}

.btn--primary:not(:disabled):hover {
  filter: brightness(0.95);
}
</style>
